import { useMemo, useState } from 'react';
import { ChevronDown, Filter, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { TagPill } from './TagPill';
import type { PickerTag } from './TagPicker';

interface Props {
  /** El vocabulario por el que se puede filtrar. */
  vocabulario: PickerTag[];
  /** Ids marcados ahora mismo. */
  valor: number[];
  onChange: (tagIds: number[]) => void;
  /** Cuántas entidades quedan tras filtrar. Sólo para el resumen. */
  resultado?: number | null;
  className?: string;
}

/**
 * Filtrar una lista por combinación de etiquetas.
 *
 * **Y lógico:** cada etiqueta que se marca estrecha la búsqueda. Marcar
 * «Verano» y «Madrid» deja lo que lleva las dos, no lo que lleva alguna — que
 * es lo que uno espera al ir marcando para acotar, y lo que hace que añadir una
 * más nunca devuelva más resultados que antes.
 *
 * Eso se dice por escrito en la pantalla, no sólo en este comentario: un filtro
 * de varias etiquetas es ambiguo a la vista, y quien marque dos y vea la lista
 * encogerse tiene que poder confirmar que es lo que pretendía.
 *
 * No se pinta si no hay vocabulario: un filtro vacío es un control que promete
 * algo y no hace nada.
 */
export function TagFilter({
  vocabulario,
  valor,
  onChange,
  resultado,
  className,
}: Props) {
  const t = useT();
  const [abierto, setAbierto] = useState(false);
  const marcadas = useMemo(() => new Set(valor), [valor]);

  if (vocabulario.length === 0) return null;

  const alternar = (id: number) =>
    onChange(valor.includes(id) ? valor.filter((v) => v !== id) : [...valor, id]);

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest transition-colors',
          valor.length
            ? 'border-[var(--color-accent-ink)] text-[var(--color-accent-ink)]'
            : 'border-[var(--color-xk-border)] text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]',
        )}
      >
        <Filter className="h-3 w-3" />
        {t('tags.filter.label')}
        {valor.length > 0 && <span className="tabular-nums">· {valor.length}</span>}
        <ChevronDown className={cn('h-3 w-3 transition-transform', abierto && 'rotate-180')} />
      </button>

      {/* Lo marcado, siempre a la vista aunque el panel esté cerrado: si el
          filtro se pliega y no queda rastro, se olvida que está puesto y la
          lista parece incompleta sin motivo. */}
      {valor.map((id) => {
        const tag = vocabulario.find((v) => v.id === id);
        if (!tag) return null;
        return (
          <TagPill
            key={id}
            name={tag.name}
            color={tag.color}
            size="sm"
            onRemove={() => alternar(id)}
          />
        );
      })}

      {valor.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--color-fg-muted)] underline-offset-2 hover:text-[var(--color-fg-primary)] hover:underline"
        >
          <X className="h-3 w-3" />
          {t('tags.filter.clear')}
        </button>
      )}

      {abierto && (
        <div className="w-full">
          <div className="mt-1 flex flex-wrap gap-1.5 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-bg-elevated)] p-2.5">
            {vocabulario.map((tag) => (
              <TagPill
                key={tag.id}
                name={tag.name}
                color={tag.color}
                size="sm"
                active={marcadas.has(tag.id)}
                onClick={() => alternar(tag.id)}
              />
            ))}
          </div>

          {/* La semántica, dicha. Con dos o más marcadas es cuando importa. */}
          {valor.length > 1 && (
            <p className="mt-1.5 text-[10px] leading-snug text-[var(--color-fg-muted)]">
              {t('tags.filter.andHint')}
            </p>
          )}
        </div>
      )}

      {valor.length > 0 && resultado != null && (
        <span className="text-[11px] tabular-nums text-[var(--color-fg-muted)]">
          {t(resultado === 1 ? 'tags.filter.oneResult' : 'tags.filter.nResults', {
            n: resultado,
          })}
        </span>
      )}
    </div>
  );
}
